import React, { useState, useEffect } from 'react';
import { Layout } from "./components/dashboard/layout";
import { Collapse, Box, Typography, Button, Tab, Tabs, Card, CardContent, Grid, TextField, Select, MenuItem, FormControl, InputLabel, Chip, Checkbox, ListItemText, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';

const StudentAttendanceDashboard = () => {
  const [students, setStudents] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [dupOpen, setDupOpen] = useState(false);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [gradeSummary, setGradeSummary] = useState([]);
  const [overallData, setOverallData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Filter states
const [searchTerm, setSearchTerm] = useState('');
const [gradeFilter, setGradeFilter] = useState([]); // Keep as array consistently
const [statusFilter, setStatusFilter] = useState('');
const [deviceFilter, setDeviceFilter] = useState([]);
const [idCardFilter, setIdCardFilter] = useState(""); // "", "Yes", "No"

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchStudentData = async () => {
    setIsLoading(true);
    try {
      const studentsResponse = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/getStudentAttendance.php');
      const summaryResponse = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/getGradeSummary.php');
      
      const studentsData = await studentsResponse.json();
      const summaryData = await summaryResponse.json();

      if (studentsData.success && Array.isArray(studentsData.data)) {
        setStudents(studentsData.data);
        setFilteredStudents(studentsData.data);
        if (studentsData.duplicates) {
          setDuplicates(studentsData.duplicates);
        }
      } else {
        setStudents([]);
        setFilteredStudents([]);
      }

      if (summaryData.success && Array.isArray(summaryData.data)) {
        setGradeSummary(summaryData.data);
        // Set as object, not array
        setOverallData(summaryData.overall || {});
      } else {
        setGradeSummary([]);
        setOverallData({});
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setStudents([]);
      setFilteredStudents([]);
      setGradeSummary([]);
      setOverallData({}); // Empty object, not array
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['PS Code', 'Grade', 'First Name', 'Last Name', 'Status', 'Check-In Time', 'Check-Out Time','Check-In Device', 'Check-Out Device'];
    
    const csvData = filteredStudents.map(student => [
      student.ps_code || '',
      student.current_grade || '',
      student.first_name || '',
      student.last_name || '',
      getAttendanceStatus(student.checkin_time, student.checkout_time),
      student.checkin_time || '',
      student.checkout_time || '',
      student.checkin_device_name || '',
      student.checkout_device_name || ''
    ]);

    csvData.unshift(headers);
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().split('T')[0] + '_' + new Date().toTimeString().slice(0, 8).replace(/:/g, '-');
    link.download = `student_attendance_${timestamp}.csv`;
    link.click();
  };

  const getAttendanceStatus = (checkinTime, checkoutTime) => {
    if (checkinTime && checkoutTime) return 'Checked Out';
    if (checkinTime) return 'On Campus';
    return 'Not Checked In';
  };

  const getStatusColor = (checkinTime, checkoutTime) => {
    if (checkinTime && checkoutTime) return '#f44336'; // Red - checked out
    if (checkinTime) return '#4caf50'; // Green - on campus
    return '#ff9800'; // Orange - not checked in
  };

  // Get unique values for filter dropdowns
  const getUniqueGrades = () => {
    const grades = [...new Set(students.map(s => s.current_grade).filter(Boolean))];

    return grades.sort((a, b) => {
      const numA = parseInt(a.replace(/[^-?\d]/g, ""), 10);
      const numB = parseInt(b.replace(/[^-?\d]/g, ""), 10);
      return numA - numB;
    });
  };

  const getUniqueDevices = () => {
    const devices = [...new Set([
      ...students.map(s => s.checkin_device_name).filter(Boolean),
      ...students.map(s => s.checkout_device_name).filter(Boolean)
    ])];
    return devices.sort();
  };

  const getUniqueStatuses = () => {
    return ['On Campus', 'Checked Out', 'Not Checked In'];
  };

  // Filter students based on search criteria
  useEffect(() => {
    let filtered = students;

    // Search term filter (searches across multiple fields)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(student => 
        (student.ps_code && student.ps_code.toLowerCase().includes(term)) ||
        (student.first_name && student.first_name.toLowerCase().includes(term)) ||
        (student.last_name && student.last_name.toLowerCase().includes(term)) ||
        (student.current_grade && student.current_grade.toLowerCase().includes(term))
      );
    }

    // Grade filter (multi-select)
    if (gradeFilter.length > 0) {
      filtered = filtered.filter(student => 
        student.current_grade && gradeFilter.includes(student.current_grade)
      );
    }

    // ID Card filter
    if (idCardFilter) {
      filtered = filtered.filter(student => {
        if (idCardFilter === "Yes") return student.has_id_card == "Yes";
        if (idCardFilter === "No") return student.has_id_card == "No" || student.has_id_card === null;
        return true; // fallback
      });
    }

    if (statusFilter && statusFilter !== "All") {
      filtered = filtered.filter(student => {
        const status = getAttendanceStatus(student.checkin_time, student.checkout_time);
        return status === statusFilter;
      });
    }

    // Device filter (checks both check-in and check-out devices)
    if (deviceFilter && deviceFilter.length > 0) {
      filtered = filtered.filter(student =>
        deviceFilter.includes(student.checkin_device_name) ||
        deviceFilter.includes(student.checkout_device_name)
      );
    }

    setFilteredStudents(filtered);
}, [students, searchTerm, gradeFilter, statusFilter, deviceFilter, idCardFilter]);

// Fixed clearFilters function
const clearFilters = () => {
  setSearchTerm('');
  setGradeFilter([]); // Set to empty array, not empty string
  setStatusFilter('');
  setDeviceFilter([]);
  setIdCardFilter('');
};

const getActiveFiltersCount = () => {
  let count = 0;
  if (searchTerm.trim()) count++; // Check for non-empty trimmed string
  if (gradeFilter.length > 0) count++;
  if (statusFilter) count++;
  if (deviceFilter.length > 0) count++;
  if (idCardFilter) count++;
  return count;
};

  const headerCellStyle = {
    padding: '12px 16px',
    borderBottom: '2px solid #ddd',
    fontWeight: '600',
    color: '#333',
    backgroundColor: '#f8f9fa',
    whiteSpace: 'nowrap'
  };

  const cellStyle = {
    padding: '10px 16px',
    borderBottom: '1px solid #eee',
    color: '#444',
    whiteSpace: 'nowrap'
  };

  const summaryCardStyle = {
    textAlign: 'center',
    minHeight: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };

  const duplicatePS = filteredStudents
  .map((s) => s.ps_code)
  .filter((ps, idx, arr) => arr.indexOf(ps) !== idx); // keeps on

  const statsData = [
    { label: "Total Students", value: overallData.total_students || 0, color: "#2196f3", icon: "👥" },
    { label: "Checked In", value: overallData.checked_in_students || 0, color: "#4caf50", icon: "✅" },
    { label: "Checked Out", value: overallData.checked_out_students || 0, color: "#f44336", icon: "🚪" },
    { label: "Still on Campus", value: overallData.still_on_campus || 0, color: "#ff9800", icon: "🏫" },
    { label: "Not Checked In", value: overallData.not_checked_in || 0, color: "#9c27b0", icon: "❌" },
    { label: "Check-in Rate", value: `${overallData.checkin_percentage || 0}%`, color: "#00bcd4", icon: "📊" }
  ];

  useEffect(() => {
    fetchStudentData();
  }, [selectedDate]);

  return (
    <Layout>
    <Box sx={{ p: 4, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Student Check-In/Check-Out Dashboard
        </Typography>
        {/*<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            label="Select Date"
            InputLabelProps={{ shrink: true }}
          />
          <Button 
            variant="contained" 
            onClick={fetchStudentData} 
            disabled={isLoading}
            sx={{ minWidth: '120px' }}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>*/}
      </Box>

      {lastUpdated && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }} style={{fontWeight: `bold`, fontSize: `20px`, color: `green`}}>
          Last updated: {lastUpdated.toLocaleTimeString()}
        </Typography>
      )}

      <div style={{ marginTop: '12px', fontSize: '14px', color: '#555', marginBottom: `20px` }}>
        {/*<div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: '#ffed77',
            border: '1px solid #ccc',
            marginRight: '8px',
            fontSize: `14px`
          }}></div>
          <span>Rows highlighted in this color are <strong>Duplicate entries</strong></span>
        </div>*/}

        <div style={{ fontStyle: 'normal', color: '#777', marginBottom: `10px`, fontSize: `14px`, marginTop: `10px` }}>
          <b>Note: </b>This report has been prepared using data captured from ID card scans at entry and exit points. Please note that there is a possibility of occasional missed scans due to human oversight.
        </div>
      </div>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}  style={{background: `#ecfbff`}}>
        <Tab label="Grade Summary" />
        {/*<Tab label={`Student Details ${activeTab === 1 ? `(${filteredStudents.length}/${students.length})` : ''}`} />*/}
        <Tab label={`Student Details`} />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          {/*<Typography variant="h5" gutterBottom>Grade-wise Check-In/Check-Out Summary</Typography>*/}
          
          {gradeSummary.length === 0 ? (
            <Typography style={{marginTop: `10px`}}>No summary data available.</Typography>
          ) : (
            <>
              {/*<Grid container spacing={3} sx={{ mb: 4 }}>
                {gradeSummary.map((grade, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Card elevation={3}>
                      <CardContent sx={summaryCardStyle}>
                        <Typography variant="h6" color="primary">
                            {grade.current_grade || 'N/A'}
                        </Typography>
                        <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                          {grade.checked_in_students || 0}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          of {grade.total_students || 0} students present
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ({grade.checkin_percentage || 0}% attendance)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>*/}

              <div style={{
                display: 'flex',
                gap: '15px',
                marginBottom: '20px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                marginTop: `20px`
              }}>
                {statsData.map((stat, index) => (
                  <div
                    key={index}
                    style={{
                      width: '200px',
                      height: '50px',
                      backgroundColor: '#ffffff',
                      border: `2px solid ${stat.color}`,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 15px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      fontSize: '20px',
                      marginRight: '10px'
                    }}>
                      {stat.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        lineHeight: '1.2',
                        marginBottom: '2px'
                      }}>
                        {stat.label}
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: stat.color,
                        lineHeight: '1'
                      }}>
                        {stat.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Box sx={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px'
                }}>
                  <thead>
                    <tr>
                      <th style={headerCellStyle}>Grade</th>
                      <th style={headerCellStyle}>Total Students</th>
                      <th style={headerCellStyle}>Checked In</th>
                      <th style={headerCellStyle}>Checked Out</th>
                      <th style={headerCellStyle}>Not Checked In</th>
                      <th style={headerCellStyle}>Still on Campus</th>
                      <th style={headerCellStyle}>Check-in %</th>
                      <th style={headerCellStyle}>Check-out %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradeSummary.map((grade, idx) => (
                      <tr key={idx} style={{ 
                        backgroundColor: idx === gradeSummary.length - 1 
                          ? '#e3f2fd' // Different color for last row
                          : idx % 2 === 0 ? '#fafafa' : '#ffffff',
                        fontSize: idx === gradeSummary.length - 1 
                          ? '18px' // Different color for last row
                          : '15px',
                        textAlign: `center`,
                        '&:hover': { backgroundColor: '#f0f0f0' }
                      }}>
                        <td style={{
                          ...cellStyle, 
                          fontWeight: idx === gradeSummary.length - 1 ? '700' : '600'
                        }}>
                          {grade.current_grade || '-'}
                        </td>
                        <td style={{
                          ...cellStyle,
                          fontWeight: idx === gradeSummary.length - 1 ? '700' : 'normal'
                        }}>
                          {grade.total_students || 0}
                        </td>
                        <td style={{
                          ...cellStyle, 
                          color: '#4caf50', 
                          fontWeight: idx === gradeSummary.length - 1 ? '700' : '600'
                        }}>
                          {grade.checked_in_students || 0}
                        </td>
                        <td style={{
                          ...cellStyle, 
                          color: '#f44336', 
                          fontWeight: idx === gradeSummary.length - 1 ? '700' : '600'
                        }}>
                          {grade.checked_out_students || 0}
                        </td>
                        <td style={{
                          ...cellStyle, 
                          color: '#ff9800', 
                          fontWeight: idx === gradeSummary.length - 1 ? '700' : '600'
                        }}>
                          {grade.not_checked_in || 0}
                        </td>
                        <td style={{
                          ...cellStyle, 
                          color: '#2196f3', 
                          fontWeight: idx === gradeSummary.length - 1 ? '700' : '600'
                        }}>
                          {grade.still_on_campus || 0}
                        </td>
                        <td style={{
                          ...cellStyle,
                          fontWeight: idx === gradeSummary.length - 1 ? '700' : 'normal',
                          position: 'relative',
                          padding: '8px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            width: '80px', // Fixed container width
                            margin: '0 auto' // Center it in the cell
                          }}>
                            <div style={{
                              width: '50px', // Fixed progress bar width
                              height: '8px',
                              backgroundColor: '#e0e0e0',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${Math.min(grade.checkin_percentage || 0, 100)}%`,
                                backgroundColor: grade.checkin_percentage >= 80 ? '#4caf50' : 
                                                grade.checkin_percentage >= 60 ? '#ff9800' : '#f44336',
                                borderRadius: '4px',
                                transition: 'width 0.3s ease'
                              }}></div>
                            </div>
                            <span style={{ 
                              width: '24px', // Fixed text width
                              fontSize: '11px',
                              fontWeight: idx === gradeSummary.length - 1 ? '700' : 'normal',
                              textAlign: 'right'
                            }}>
                              {grade.checkin_percentage || 0}%
                            </span>
                          </div>
                        </td>
                        <td style={{
                          ...cellStyle,
                          fontWeight: idx === gradeSummary.length - 1 ? '700' : 'normal',
                          position: 'relative',
                          padding: '8px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            width: '80px', // Fixed container width
                            margin: '0 auto' // Center it in the cell
                          }}>
                            <div style={{
                              width: '50px', // Fixed progress bar width
                              height: '8px',
                              backgroundColor: '#e0e0e0',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${Math.min(grade.checkout_percentage || 0, 100)}%`,
                                backgroundColor: grade.checkout_percentage >= 50 ? '#4caf50' : 
                                                grade.checkout_percentage >= 20 ? '#ff9800' : '#f44336',
                                borderRadius: '4px',
                                transition: 'width 0.3s ease'
                              }}></div>
                            </div>
                            <span style={{ 
                              width: '24px', // Fixed text width
                              fontSize: '11px',
                              fontWeight: idx === gradeSummary.length - 1 ? '700' : 'normal',
                              textAlign: 'right'
                            }}>
                              {grade.checkout_percentage || 0}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {/*<Button onClick={exportToCSV} variant="contained" style={{marginBottom: `10px`,float: `right`,background: `#298cb4`}}>
            Download CSV
          </Button>*/}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>

            {getActiveFiltersCount() > 0 && (
              <Button 
                variant="outlined" 
                onClick={clearFilters} 
                size="small"
                color="error"
                startIcon={<span>🗑️</span>}
                style={{marginTop: `20px`}}
              >
                Clear All Filters
              </Button>
            )}
          </Box>

          {/* Filter Controls */}
            <Box display="flex" alignItems="flex-start" gap={3} flexWrap="wrap" mb={2} style={{marginTop: `10px`}}>
                {/* Grade */}
                <Box width="20%">
                  <Typography variant="body2" gutterBottom style={{ fontWeight: "bold" }}>
                    Grade
                  </Typography>
                  <Select
                    multiple
                    size="small"
                    value={gradeFilter}
                    onChange={(e) =>
                      setGradeFilter(
                        typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value
                      )
                    }
                    renderValue={(selected) => {
                      if (selected.length === 0) return "All Grades";
                      if (selected.length === 1) return selected[0];
                      return `${selected.length} grades selected`;
                    }}
                    sx={{ minWidth: 150 }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                    fullWidth
                  >
                    {getUniqueGrades().map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        <Checkbox checked={gradeFilter.indexOf(grade) > -1} size="small" />
                        <ListItemText primary={grade} />
                      </MenuItem>
                    ))}
                  </Select>

                </Box>

                {/* Search */}
                <Box width="20%">
                  <Typography variant="body2" gutterBottom style={{ fontWeight: "bold" }}>
                    Search
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Student Number (or) Name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                  />
                </Box>

                {/* Status */}
                <Box width="20%">
                  <Typography variant="body2" gutterBottom style={{ fontWeight: "bold" }}>
                    Status
                  </Typography>
                  <Select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    renderValue={(selected) => {
                      if (!selected || selected === "All") return "All";
                      return selected;
                    }}
                    sx={{ minWidth: 150 }}
                    fullWidth
                  >
                    <MenuItem value="All">
                      <ListItemText primary="All" />
                    </MenuItem>

                    {getUniqueStatuses().map((status) => (
                      <MenuItem key={status} value={status}>
                        <ListItemText primary={status} />
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Device */}
                <Box width="20%">
                  <Typography variant="body2" gutterBottom style={{ fontWeight: "bold" }}>
                    Device
                  </Typography>
                  <Select
                    multiple
                    size="small"
                    value={deviceFilter}
                    onChange={(e) =>
                      setDeviceFilter(
                        typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value
                      )
                    }
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) return "All Devices";
                      if (selected.length === 1) return selected[0];
                      return `${selected.length} devices selected`;
                    }}
                    sx={{ minWidth: 150 }}
                    fullWidth
                  >
                    <MenuItem value="">
                      <Checkbox checked={deviceFilter.length === 0} />
                      <ListItemText primary="All Devices" />
                    </MenuItem>
                    {getUniqueDevices().map((device) => (
                      <MenuItem key={device} value={device}>
                        <Checkbox checked={deviceFilter.includes(device)} />
                        <ListItemText primary={device} />
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Box width="10%">
                  <Typography variant="body2" gutterBottom style={{ fontWeight: "bold" }}>
                    ID Card
                  </Typography>
                  <Select
                    size="small"
                    value={idCardFilter}
                    onChange={(e) => setIdCardFilter(e.target.value)}
                    sx={{ minWidth: 150 }}
                    fullWidth
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </Box>
              </Box>

          {/*{getActiveFiltersCount() > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">Active filters:</Typography>
              {searchTerm.trim() && (
                <Chip 
                  label={`Search: "${searchTerm}"`} 
                  size="small" 
                  onDelete={() => setSearchTerm('')}
                  color="primary"
                />
              )}
              {gradeFilter.length > 0 && (
                <Chip 
                  label={`Grades: ${gradeFilter.length === 1 ? gradeFilter[0] : `${gradeFilter.length} selected`}`}
                  size="small" 
                  onDelete={() => setGradeFilter([])}
                  color="primary"
                />
              )}
              {statusFilter && (
                <Chip 
                  label={`Status: ${statusFilter}`} 
                  size="small" 
                  onDelete={() => setStatusFilter('')}
                  color="primary"
                />
              )}
              {deviceFilter && (
                <Chip 
                  label={`Device: ${deviceFilter}`} 
                  size="small" 
                  onDelete={() => setDeviceFilter('')}
                  color="primary"
                />
              )}
            </Box>
          )}*/}

          {/*Result Summary*/}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Showing {filteredStudents.length} of {students.length} entries
          </Typography>
          
          {/* Duplicate Section 
            {duplicates.length > 0 && (
                <>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDupOpen(!dupOpen)}
                >
                  ⚠️ &nbsp; {duplicates.length} Duplicate
                  {duplicates.length > 1 ? "s" : ""}
                </Button>

                <Collapse in={dupOpen} timeout="auto" unmountOnExit>
                  <Typography
                    variant="h6"
                    color="error"
                    sx={{ mt: 2, mb: 1 }}
                  >
                  Duplicate Entries Detected
                  </Typography>
                  <Table style={{width: `40%`}}>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{fontWeight: `bold`}}>Student Number</TableCell>
                        <TableCell style={{fontWeight: `bold`}}>Student Name</TableCell>
                        <TableCell style={{fontWeight: `bold`}} align="center">Duplicate Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {duplicates.map((dup, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{dup.ps_code}</TableCell>
                          <TableCell>
                            {dup.first_name} {dup.last_name}
                          </TableCell>
                          <TableCell align="center">
                            {dup.row_count}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Collapse>
                </>
            )}*/}

          <Box sx={{ overflowX: 'auto', marginTop: `10px` }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              textAlign: `center`
            }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Student Number</th>
                  <th style={headerCellStyle}>Grade</th>
                  <th style={headerCellStyle}>First Name</th>
                  <th style={headerCellStyle}>Last Name</th>
                  <th style={headerCellStyle}>Status</th>
                  <th style={headerCellStyle}>ID Card (Check-In)</th>
                  <th style={headerCellStyle}>Check-In Time</th>
                  <th style={headerCellStyle}>Check-In Device</th>
                  <th style={headerCellStyle}>Check-Out Time</th>
                  <th style={headerCellStyle}>Check-Out Device</th>
                  
                </tr>
              </thead>
              <tbody>
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                      {isLoading
                        ? 'Loading student data...'
                        : students.length === 0
                        ? 'No student data available.'
                        : 'No students match the current filters.'}
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student, idx) => {
                    const isDuplicate = duplicatePS.includes(student.ps_code);

                    return (
                      <tr
                        key={idx}
                        style={{
                          backgroundColor: idx % 2 === 0
                            ? '#fafafa'
                            : '#ffffff',
                          fontWeight: 'normal',
                        }}
                      >
                        <td style={cellStyle}>{student.ps_code || '-'}</td>
                        <td style={cellStyle}>{student.current_grade || '-'}</td>
                        <td style={cellStyle}>{student.first_name || '-'}</td>
                        <td style={cellStyle}>{student.last_name || '-'}</td>
                        <td
                          style={{
                            ...cellStyle,
                            color: getStatusColor(student.checkin_time, student.checkout_time),
                            fontWeight: '600',
                          }}
                        >
                          {getAttendanceStatus(student.checkin_time, student.checkout_time)}
                        </td>
                        <td style={cellStyle}>
                          {student.checkin_device_name ? student.has_id_card : "-"}
                        </td>
                        <td style={cellStyle}>
                          {student.checkin_time
                            ? new Date(student.checkin_time).toLocaleTimeString()
                            : '-'}
                        </td>
                        <td style={cellStyle}>{student.checkin_device_name || '-'}</td>
                        <td style={cellStyle}>
                          {student.checkout_time
                            ? new Date(student.checkout_time).toLocaleTimeString()
                            : '-'}
                        </td>
                        <td style={cellStyle}>{student.checkout_device_name || '-'}</td>
                        
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </Box>
    </Layout>
  );
};

export default StudentAttendanceDashboard;